import { useState, useEffect } from 'react';
import RestoApiService, { LatestReport } from '../utils/RestoApiService';
import ErrorHandler from '../utils/ErrorHandler';

interface UseLatestReportsState {
  reports: LatestReport[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for fetching and managing latest reports data
 * Provides loading states, error handling, and refetch functionality.
 * Pass `initialReports` to seed from SSR and skip the first client fetch.
 */
export const useLatestReports = (initialReports?: LatestReport[]): UseLatestReportsState => {
  const hasInitial = !!initialReports?.length;
  const [reports, setReports] = useState<LatestReport[]>(initialReports ?? []);
  const [loading, setLoading] = useState<boolean>(!hasInitial);
  const [error, setError] = useState<string | null>(null);

  const apiService = new RestoApiService();

  const fetchReports = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.fetchLatestReports();
      
      if (response.success && response.data) {
        setReports(response.data);
      } else {
        const errorMessage = ErrorHandler.formatErrorMessage(response.error || 'Failed to fetch latest reports');
        setError(errorMessage);
        setReports([]);
        ErrorHandler.logError(response.error || 'API response error', 'useLatestReports.fetchReports');
      }
    } catch (err) {
      const errorMessage = ErrorHandler.formatErrorMessage(err);
      setError(errorMessage);
      setReports([]);
      ErrorHandler.logError(err, 'useLatestReports.fetchReports');
    } finally {
      setLoading(false);
    }
  };

  // Fetch reports on component mount — skip if SSR already seeded
  useEffect(() => {
    if (!hasInitial) fetchReports();
  }, []);

  return {
    reports,
    loading,
    error,
    refetch: fetchReports,
  };
};

export default useLatestReports;